import {
    addEventListenerList,
    makeTabbable,
    makeUntabbable
} from '../utilities/utilities';

/**
 * Consult README.md for compilation/transpilation instructions.
 * Contains JS functionality for thanks page which appears at
 * the end of the res sign up journey (plus.net/signup/thanks/)
 */
export default class DataCaptureForm {
    /**
     * @return {void}
     */
    constructor () {
        this.mainWrapper = document.querySelector('.js-pn-mobile');
        this.adestraIframe = document.querySelector('.js-adestra');

        this.blocks = {
            intro: document.querySelector('.js-mobile-info'),
            form: document.querySelector('.js-data-capture'),
            sending: document.querySelector('.js-data-capture-sending'),
            success: document.querySelector('.js-data-capture-success'),
            failed: document.querySelector('.js-data-capture-failed'),
        };

        this.inputs = {
            firstName: document.querySelector('.js-first-name'),
            surname: document.querySelector('.js-surname'),
            email: document.querySelector('.js-email'),
            month: document.querySelector('.js-mobile-month'),
            year: document.querySelector('.js-mobile-year'),
            provider: document.querySelector('.js-mobile-provider'),
            contract: document.querySelector('.js-mobile-contract'),
        };

        this.buttons = {
            viewDeals: document.querySelectorAll('.js-mobile-view-deals'),
            remindMe: document.querySelector('.js-mobile-remind'),
            submit: document.querySelector('.js-data-capture-submit'),
            reset: document.querySelector('.js-reset-button'),
        };
    }

    /**
     * @return {void}
     */
    init () {
        this.setupEvents();
    }

    /**
     * @return {void}
     */
    setupEvents () {
        addEventListenerList(this.buttons.viewDeals, 'click', () => {
            this.setUserDataCookie();
        });

        // button isn't rendered on page if user hasn't opted into marketing
        if (this.buttons.remindMe !== null) {
            this.buttons.remindMe.addEventListener('click', () => {
                this.hideAllBlocks();
                this.showBlock('form');
            });
        }

        this.inputs.month.addEventListener('change', () => {
            this.runValidation();
        });

        this.inputs.year.addEventListener('change', () => {
            this.runValidation();
        });

        this.buttons.submit.addEventListener('click', () => {
            this.submitForm();
        });

        this.buttons.reset.addEventListener('click', () => {
            this.hideAllBlocks();
            this.showBlock('intro');
        });
    }

    /**
     * Show a single block
     *
     * @param  {string} blockName key of block to show in this.blocks
     * @return {void}
     */
    showBlock (blockName) {
        this.blocks[blockName].classList.add('is_active');
        this.blocks[blockName].setAttribute('aria-hidden', false);
        makeTabbable(this.blocks[blockName]);
    }

    /**
     * Show multiple blocks
     *
     * @param  {array} blocksToShow keys of blocks to show in this.blocks
     * @return {void}
     */
    showBlocks (blocksToShow = []) {
        for (let i = 0; i < blocksToShow.length; i++) {
            this.showBlock(blocksToShow[i]);
        }
    }

    /**
     * Hide a single block
     *
     * @param  {string} blockName key of block to hide in this.blocks
     * @return {void}
     */
    hideBlock (blockName) {
        this.blocks[blockName].classList.remove('is_active');
        this.blocks[blockName].setAttribute('aria-hidden', true);
        makeUntabbable(this.blocks[blockName]);
    }

    /**
     * Hide multiple blocks
     *
     * @param  {array} blocksToHide keys of blocks to hide in this.blocks
     * @return {void}
     */
    hideBlocks (blocksToHide = []) {
        for (let i = 0; i < blocksToHide.length; i++) {
            this.hideBlock(blocksToHide[i]);
        }
    }

    /**
     * Hide all blocks
     *
     * @param  {array} blocksToHide keys of blocks to hide in this.blocks
     * @return {void}
     */
    hideAllBlocks () {
        let key;

        for (key in this.blocks) {
            if (Object.prototype.hasOwnProperty.call(this.blocks, key)) {
                this.hideBlock(key);
            }
        }
    }

    /**
     * Show a transparent block such as sending or failed
     *
     * @param  {string} blockName sending or failed
     * @return {void}
     */
    showOverlayBlock (blockName) {
        this.hideBlocks([
            'intro',
            'sending',
            'failed',
            'success',
        ]);

        this.showBlock('form');
        makeUntabbable(this.blocks.form);
        this.showBlock(blockName);
    }

    /**
     * Check form is valid and add/remove appropriate classes
     * @return {void}
     */
    runValidation () {
        if (this.isFormValid()) {
            this.buttons.submit.classList.remove('is_disabled');
            this.inputs.month.classList.remove('is_invalid');
            this.inputs.year.classList.remove('is_invalid');
        } else {
            let methodName = (!this.isMonthValid()) ? 'add' : 'remove';

            this.inputs.month.classList[methodName]('is_invalid');

            methodName = (!this.isYearValid()) ? 'add' : 'remove';

            this.inputs.year.classList[methodName]('is_invalid');

            this.buttons.submit.classList.add('is_disabled');
        }
    }

    /**
     * @return {boolean}
     */
    isFormValid () {
        return (this.isMonthValid() && this.isYearValid());
    }

    /**
     * @return {boolean}
     */
    isMonthValid () {
        return (this.inputs.month.value !== '0');
    }

    /**
     * @return {boolean}
     */
    isYearValid () {
        return (this.inputs.year.value !== '0');
    }

    /**
     * @return {void}
     */
    submitForm () {
        this.runValidation();

        if (!this.isFormValid()) {
            this.sendFormValidationFailedEvent();

            return;
        }

        this.showOverlayBlock('sending');
        this.sendToAdestra();
    }

    /**
     * Package form data in a JSON string and send to the iFrame
     * @return {void}
     */
    sendToAdestra () {
        const formData = JSON.stringify(this.getFormValues());

        window.addEventListener(
            'message',
            response => {
                this.handleAdestraResponse(response);
            },
            false
        );

        this.adestraIframe.contentWindow.postMessage(
            formData, 'https://comm.plus.net'
        );
    }

    /**
     * Check response from adestra and show success or failed feedback
     *
     * @param {string} response return data from Adestra after form submit
     * @return {void}
     */
    handleAdestraResponse (response) {
        if (response.data === 'SUCCESS') {
            this.hideAllBlocks();
            this.showBlock('success');
            this.sendFormSuccessEvent();
        } else if (response.data.substr(0, 5) === 'ERROR') {
            this.showOverlayBlock('failed');
            this.sendFormAdestraFailedEvent();
        }
    }

    /**
     * @return {object}
     */
    getFormValues () {
        return {
            email: this.inputs.email.value,
            firstName: this.inputs.firstName.value,
            surname: this.inputs.surname.value,
            month: this.inputs.month.value,
            year: this.inputs.year.value,
            provider: this.inputs.provider.value,
            contract: this.inputs.contract.value,
        };
    }

    /**
     * Send GA event for successful submission
     * @return {void}
     */
    sendFormSuccessEvent () {
        const eventData = {
            event: 'formSubmitted',
            formAction: '/signup/thanks/',
            inputNames: this.getCompletedFields(),
        };

        this.sendDataLayerEvent(eventData);
    }

    /**
     * Send GA event when it fails to send data to Adestra
     * @return {void}
     */
    sendFormAdestraFailedEvent () {
        const eventData = {
            event: 'formToAdestraFailed',
            formAction: '/signup/thanks/',
            inputNames: this.getCompletedFields(),
        };

        this.sendDataLayerEvent(eventData);
    }

    /**
     * Send GA event when user hits submit and validation fails
     * @return {void}
     */
    sendFormValidationFailedEvent () {
        const eventData = {
            event: 'formValidationFailed',
            formAction: '/signup/thanks/',
            inputNames: this.getFailedFields(),
        };

        this.sendDataLayerEvent(eventData);
    }

    /**
     * Send GA events
     *
     * @param  {object} eventData object of key/values to send to GA
     * @return {void}
     */
    sendDataLayerEvent (eventData) {
        if (typeof window.dataLayer !== 'undefined') {
            window.dataLayer.push(eventData);
        }
    }

    /**
     * Get comma separated list of field names which a user filled in
     * @return {string}
     */
    getCompletedFields () {
        const completedFields = ['mobile_month', 'mobile_year'];

        // Optional fields
        if (this.inputs.provider.value !== '0') {
            completedFields.push('mobile_provider');
        }
        if (this.inputs.contract.value !== '0') {
            completedFields.push('mobile_contract');
        }

        return completedFields.join(', ');
    }

    /**
     * Get comma separated list of field names which failed validation
     * @return {string}
     */
    getFailedFields () {
        const failedFields = [];

        if (!this.isMonthValid()) {
            failedFields.push('mobile_month');
        }

        if (!this.isYearValid()) {
            failedFields.push('mobile_year');
        }

        return failedFields.join(', ');
    }

    /**
     * Create cookie with username and postcode
     * to share data with Plusnet Mobile site.
     *
     * @return {void}
     */
    setUserDataCookie () {
        const matesRatesData = this.mainWrapper.dataset.matesRates;

        window.clientData.storage.setData({
            name: 'matesRatesData',
            value: matesRatesData,
            hours: 3,
            path: '/',
        }, 'cookie');
    }
}
